import { Routes, ɵEmptyOutletComponent } from '@angular/router';
import { simpleMetaResolver } from '../account/resolves/simple-meta-resolver';
import { AssetPageComponent } from '../gallery-shared/pages/asset-page/asset-page.component';
import { AssetVersionPageComponent } from '../gallery-shared/pages/asset-version-page/asset-version-page.component';
import { AssetPageActivation } from '../gallery/guards/asset-activate.guard';
import { AssetPageDeactivation } from '../gallery/guards/asset-deactivate.guard';
import { AssetVersionDeactivateGuard } from '../gallery/guards/asset-version-deactivate.guard';
import { AssetVersionGuard } from '../gallery/guards/asset-version.guard';
import { TranslationsLoadedGuard } from '../gallery/guards/translations-loaded.guard';
import { noIndextenantProfilePageMetaResolver } from '../gallery/resolves/noindex-tenant-profile-page-meta.resolver';
import { tenantProfilePageMetaResolver } from '../gallery/resolves/tenant-profile-page-meta.resolver';
import { GalleryAccessGuard } from '../guards/gallery-access.guard';
import { GalleryNotFoundGuard } from '../guards/gallery-not-found.guard';
import { getMetaImageTags, noIndexMetaTag } from '../lib/meta-helpers';
import { GalleryAdvancedGuard } from './guards/gallery-advanced.guard';
import { GalleryErrorGuard } from './guards/gallery-error.guard';
import { GalleryIdentifyGuard } from './guards/gallery-identify.guard';
import { GalleryMaintenanceGuard } from './guards/gallery-maintenance.guard';
import { GalleryReviewGuard } from './guards/gallery-review-guard';
import { LegacyGalleryEmbedRedirectGuard } from './guards/legacy-gallery-embed-guard';
import { mainGalleryCanMatchGuard } from './guards/main-gallery-can-match.guard';
import { TenantProfileMaintenanceGuard } from './guards/tenant-profile-maintenance.guard';
import { TenantPublicGuard } from './guards/tenant-public.guard';
import { ViewGalleryIdentificationGuard } from './guards/view-gallery-identification.guard';
import { ViewGalleryRedirectGuard } from './guards/view-gallery-redirect.guard';
import { ViewGalleryGuard } from './guards/view-gallery.guard';
import { GalleryDownloadPageComponent } from './pages/gallery-download-page/gallery-download-page.component';
import { GalleryErrorPageComponent } from './pages/gallery-error-page/gallery-error-page.component';
import { GalleryIdentifyPageComponent } from './pages/gallery-identify-page/gallery-identify-page.component';
import { GalleryMaintenancePageComponent } from './pages/gallery-maintenance-page/gallery-maintenance-page.component';
import { GalleryNotFoundPageComponent } from './pages/gallery-not-found-page/gallery-not-found-page.component';
import { GalleryPasswordPageComponent } from './pages/gallery-password-page/gallery-password-page.component';
import { GalleryPwaPageComponent } from './pages/gallery-pwa-page/gallery-pwa-page.component';
import { MainGalleryProxyPageComponent } from './pages/main-gallery-proxy-page/main-gallery-proxy-page.component';
import { SubscribeToAssetComponent } from './pages/subscribe-to-asset-page/subscribe-to-asset-page.component';
import { TenantProfileMaintenancePageComponent } from './pages/tenant-profile-maintenance-page/tenant-profile-maintenance-page.component';
import { TenantProfilePageComponent } from './pages/tenant-profile-page/tenant-profile-page.component';
import { UnsubscribedBasePageComponent } from './pages/unsubscribed-base-page/unsubscribed-base-page.component';
import { UnsubscribedFromAssetComponent } from './pages/unsubscribed-from-asset-page/unsubscribed-from-asset-page.component';
import { UnsubscribedFromGalleryPageComponent } from './pages/unsubscribed-from-gallery-page/unsubscribed-from-gallery-page.component';
import { ViewGalleryAdvancedModePageComponent } from './pages/view-gallery-advanced-mode-page/view-gallery-advanced-mode-page.component';
import { ViewGalleryBasePageComponent } from './pages/view-gallery-base-page/view-gallery-base-page.component';
import { ViewGalleryPageComponent } from './pages/view-gallery-page/view-gallery-page.component';
import { publicAssetPageMetaResolver } from './resolves/public-asset-page-meta.resolver';
import { publicGalleryPageMetaResolver } from './resolves/public-gallery-page-meta.resolver';
import { TenantProfileResolve } from './resolves/tenant-profile.resolve';
import { ViewGalleryAssetVersionResolve } from './resolves/view-gallery-asset-version.resolve';
import { ViewGalleryAssetResolve } from './resolves/view-gallery-asset.resolve';
import { ViewGallerySectionsResolve } from './resolves/view-gallery-sections.resolve';
import { ViewGalleryResolve } from './resolves/view-gallery.resolve';

const galleryPathRoutes: Routes = [
  // gallery actions
  {
    path: 'access/:galleryPath',
    canActivate: [GalleryAccessGuard],
    resolve: { tenant: TenantProfileResolve },
    data: {
      meta: simpleMetaResolver('Protected Gallery', 'This is a protected gallery which requires a password to view', [
        ...getMetaImageTags('https://picflow.com/assets/sharing/protected.png'),
        noIndexMetaTag,
      ]),
    },
    component: GalleryPasswordPageComponent,
    children: [
      {
        path: '**',
        component: GalleryPasswordPageComponent,
      },
    ],
  },
  {
    path: 'identify/:galleryPath',
    canActivate: [GalleryIdentifyGuard],
    resolve: {
      gallery: ViewGalleryResolve,
      meta: publicGalleryPageMetaResolver,
    },
    component: GalleryIdentifyPageComponent,
    data: {
      meta: simpleMetaResolver(`Gallery · Identify`),
    },
    children: [
      {
        path: '**',
        component: GalleryIdentifyPageComponent,
      },
    ],
  },
  {
    path: 'not-found/:galleryPath',
    canActivate: [GalleryNotFoundGuard],
    resolve: { tenant: TenantProfileResolve },
    component: GalleryNotFoundPageComponent,
    data: {
      meta: simpleMetaResolver(`Gallery · Not Found`, undefined, [noIndexMetaTag]),
    },
    children: [
      {
        path: '**',
        component: GalleryNotFoundPageComponent,
      },
    ],
  },
  {
    path: 'error/:galleryPath',
    canActivate: [GalleryErrorGuard],
    resolve: { tenant: TenantProfileResolve },
    component: GalleryErrorPageComponent,
    data: {
      meta: simpleMetaResolver(`Gallery · Error`, undefined, [noIndexMetaTag]),
    },
    children: [
      {
        path: '**',
        component: GalleryErrorPageComponent,
      },
    ],
  },
  {
    path: 'maintenance/:galleryPath',
    canActivate: [GalleryMaintenanceGuard],
    component: GalleryMaintenancePageComponent,
    data: {
      meta: simpleMetaResolver(`Gallery · Maintenance`, undefined, [noIndexMetaTag]),
    },
    children: [
      {
        path: '**',
        component: GalleryMaintenancePageComponent,
      },
    ],
  },
  {
    path: ':galleryPath',
    component: ViewGalleryBasePageComponent,
    canActivate: [ViewGalleryGuard],
    resolve: {
      gallery: ViewGalleryResolve,
      meta: publicGalleryPageMetaResolver,
    },
    children: [
      {
        path: 'pwa',
        component: GalleryPwaPageComponent,
        children: [
          {
            path: '**',
            component: ɵEmptyOutletComponent,
          },
        ],
      },
      {
        path: 'download',
        component: GalleryDownloadPageComponent,
      },
      {
        path: 'unsubscribe',
        component: UnsubscribedBasePageComponent,
        data: {
          meta: simpleMetaResolver(`Gallery · Unsubscribe`),
        },
        children: [
          {
            path: '',
            component: UnsubscribedFromGalleryPageComponent,
          },
          {
            path: ':assetId',
            component: UnsubscribedFromAssetComponent,
          },
        ],
      },
      {
        path: 'advanced',
        component: ViewGalleryAdvancedModePageComponent,
        canActivate: [GalleryAdvancedGuard],
        children: [
          {
            path: ':assetId',
            component: AssetPageComponent,
            resolve: {
              data: ViewGalleryAssetResolve,
              meta: publicAssetPageMetaResolver,
            },
            canActivate: [AssetPageActivation],
            canDeactivate: [AssetPageDeactivation],
          },
          {
            path: ':assetId/version/:versionId',
            component: AssetVersionPageComponent,
            resolve: { data: ViewGalleryAssetVersionResolve },
            canActivate: [AssetVersionGuard],
            canDeactivate: [AssetVersionDeactivateGuard],
          },
        ],
      },
      {
        path: 'embed',
        canActivate: [LegacyGalleryEmbedRedirectGuard],
        component: ɵEmptyOutletComponent,
      },
      {
        path: 'review/:code',
        canActivate: [GalleryReviewGuard],
        children: [
          {
            path: '',
            component: ɵEmptyOutletComponent,
          },
          {
            path: '**',
            component: ɵEmptyOutletComponent,
          },
        ],
      },
      {
        path: 'subscribe/:assetId',
        component: SubscribeToAssetComponent,
        data: {
          meta: simpleMetaResolver(`Gallery · Subscribe`, undefined, [noIndexMetaTag]),
        },
      },
      {
        path: '',
        component: ViewGalleryPageComponent,
        resolve: {
          sections: ViewGallerySectionsResolve,
        },
        children: [
          {
            path: ':assetId',
            component: AssetPageComponent,
            resolve: {
              data: ViewGalleryAssetResolve,
              meta: publicAssetPageMetaResolver,
            },
            canActivate: [AssetPageActivation],
            canDeactivate: [AssetPageDeactivation],
          },
          {
            path: ':assetId/version/:versionId',
            component: AssetVersionPageComponent,
            resolve: { data: ViewGalleryAssetVersionResolve },
            canActivate: [AssetVersionGuard],
            canDeactivate: [AssetVersionDeactivateGuard],
          },
        ],
      },
    ],
  },
];

export const routes: Routes = [
  {
    path: '',
    canActivate: [TenantPublicGuard],
    children: [
      {
        path: '',
        canActivate: [ViewGalleryRedirectGuard, ViewGalleryIdentificationGuard, TranslationsLoadedGuard],
        resolve: {
          tenant: TenantProfileResolve,
        },
        children: [
          // tenant pages
          {
            path: '',
            component: TenantProfilePageComponent,
            resolve: {
              tenant: TenantProfileResolve,
              meta: tenantProfilePageMetaResolver,
            },
          },
          {
            path: 'pwa',
            component: TenantProfilePageComponent,
            resolve: {
              tenant: TenantProfileResolve,
              meta: noIndextenantProfilePageMetaResolver,
            },
          },
          {
            path: 'maintenance',
            component: TenantProfileMaintenancePageComponent,
            canActivate: [TenantProfileMaintenanceGuard],
            data: {
              meta: simpleMetaResolver(`Maintenance`),
            },
          },
          // sub gallery
          {
            path: ':mainGalleryPath',
            component: MainGalleryProxyPageComponent,
            canMatch: [mainGalleryCanMatchGuard],
            children: [...galleryPathRoutes],
          },
          // main gallery
          ...galleryPathRoutes,
        ],
      },
    ],
  },
];
